import React, { useCallback, useMemo, useState } from "react";
import plur from "plur";
import { Button, Flex, SegmentedControl, Text } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { CurrencyCode } from "#graphql/supergraphEnums";
import { sortByPaymentInstructionsUpdated } from "../../../DistributionWorkflow/StepComponents/SelectPositionsStep/SelectPositionsTable/utils";
import Money, { MoneyType } from "../../../Money";
import { addMoney, DEFAULT_PRECISION } from "../../../Wallet/utils";
import { mapAccountingPayableToPositionRows } from "../../adapters/accountingPayableAdapter";
import { PaymentBatchActionTypes } from "../../Context/constants";
import { usePaymentBatchDataContext, usePaymentBatchDispatch } from "../../Context/context";
import { PaymentBatchWorkflowStepProps, PositionRowData } from "../../types";
import { SelectPositionsTable } from "./SelectPositionsTable";
import { PositionTabStatus } from "./SelectPositionsTable/constants";
import {
  buttons,
  container,
  contentContainer,
  fieldLabel,
  footer,
  headerContainer,
  linkButton,
} from "./styles_vanilla.css";

const DEFAULT_TAB_STATUS = PositionTabStatus.Available;

const SelectPositionsStep: React.FC<PaymentBatchWorkflowStepProps> = ({
  onBack,
  onContinue,
}: PaymentBatchWorkflowStepProps) => {
  const { batchData } = usePaymentBatchDataContext();
  const dispatch = usePaymentBatchDispatch();

  const paymentsInstructionScreening = useFeatureFlag("paymentsInstructionScreening", false);

  // Derive position rows from context (populated by parent)
  const rawPositionRows = useMemo(() => {
    if (!batchData) return [];
    return mapAccountingPayableToPositionRows(batchData, { paymentsInstructionScreening });
  }, [batchData, paymentsInstructionScreening]);

  const loading = !batchData;
  const isArenaIdentified = !!batchData;

  const zeroAmount = useMemo<MoneyType>(
    () => ({
      amount: 0,
      currency: batchData?.entity?.currency?.code || CurrencyCode.Usd,
      precision: DEFAULT_PRECISION,
    }),
    [batchData?.entity?.currency?.code],
  );

  const [selectedSum, setSelectedSum] = useState<MoneyType>(zeroAmount);

  // Sort position rows by recently-updated payment instructions
  const positionRows = useMemo<PositionRowData[]>(() => {
    if (!isArenaIdentified) return [];
    return [...rawPositionRows].sort(sortByPaymentInstructionsUpdated);
  }, [isArenaIdentified, rawPositionRows]);

  const availableToPayPositions = useMemo(
    () => positionRows.filter((row) => row.positionTabStatus === PositionTabStatus.Available),
    [positionRows],
  );

  const paidPositions = useMemo(
    () => positionRows.filter((row) => row.positionTabStatus === PositionTabStatus.Paid),
    [positionRows],
  );

  const unableToPayPositions = useMemo(
    () => positionRows.filter((row) => row.positionTabStatus === PositionTabStatus.UnableToPay),
    [positionRows],
  );

  const [selectedPositionRows, setSelectedPositionRows] = useState<PositionRowData[]>([]);

  const onSelectionChange = useCallback(
    (selectedRows: PositionRowData[]) => {
      let newSum = [{ ...zeroAmount }];
      selectedRows.forEach((row) => {
        newSum = addMoney(newSum, [row.netPayment]);
      });
      setSelectedSum({ ...newSum[0] });
      setSelectedPositionRows(selectedRows);
    },
    [zeroAmount],
  );

  const selectedPositionsCount = selectedPositionRows.length;

  const [tabStatus, setTabStatus] = useState<PositionTabStatus>(DEFAULT_TAB_STATUS);
  const tabOptions = useMemo(
    () => [
      {
        value: PositionTabStatus.Available,
        label: `Available to pay (${availableToPayPositions.length})`,
      },
      {
        value: PositionTabStatus.UnableToPay,
        label: `Unable to pay (${unableToPayPositions.length})`,
      },
      {
        value: PositionTabStatus.Paid,
        label: `Paid (${paidPositions.length})`,
      },
    ],
    [availableToPayPositions.length, paidPositions.length, unableToPayPositions.length],
  );

  const handleContinue = useCallback(() => {
    setTabStatus(DEFAULT_TAB_STATUS);
    dispatch({
      type: PaymentBatchActionTypes.SET_SELECT_POSITIONS_DATA,
      payload: {
        selectedPositions: selectedPositionRows,
        numberOfAvailablePositions: availableToPayPositions.length,
      },
    });
    onContinue();
  }, [onContinue, selectedPositionRows, availableToPayPositions, dispatch]);

  const handleBack = useCallback(() => {
    setTabStatus(DEFAULT_TAB_STATUS);
    onBack();
  }, [onBack]);

  return (
    <Flex className={container} flexDirection="column">
      <Flex className={headerContainer} justifyContent="space-between">
        <Text variant="h2" data-testid="select-positions-title">
          Positions
        </Text>
        <SegmentedControl
          onChange={(option: string) => {
            setTabStatus(option as PositionTabStatus);
          }}
          value={tabStatus}
          options={tabOptions}
        />
      </Flex>
      <Flex className={contentContainer} flexDirection="column">
        {tabStatus === PositionTabStatus.Available && (
          <SelectPositionsTable
            allowSelection={true}
            emptyTableMessage="You have no positions available to be paid"
            positionRowData={availableToPayPositions}
            handleSelectionChange={onSelectionChange}
            loading={loading || !isArenaIdentified}
          />
        )}
        {tabStatus === PositionTabStatus.Paid && (
          <SelectPositionsTable
            allowSelection={false}
            emptyTableMessage="You have no positions that have been paid"
            positionRowData={paidPositions}
            loading={loading || !isArenaIdentified}
          />
        )}
        {tabStatus === PositionTabStatus.UnableToPay && (
          <SelectPositionsTable
            allowSelection={false}
            emptyTableMessage="You have no positions that are unable to be paid"
            positionRowData={unableToPayPositions}
            loading={loading || !isArenaIdentified}
            showStatusColumn={true}
          />
        )}
      </Flex>
      <Flex className={footer} justifyContent="space-between">
        <Flex flexDirection="row">
          <Flex>
            <Button
              onClick={handleBack}
              data-cy="back-button"
              data-testid="back-button"
              variant="neutral"
              className={buttons}
            >
              Back
            </Button>
          </Flex>
        </Flex>
        <Flex flexDirection="row-reverse">
          <Flex>
            <Button
              onClick={handleContinue}
              data-cy="continue-button"
              data-testid="continue-button"
              className={buttons}
              disabled={selectedPositionsCount === 0}
            >
              Continue
            </Button>
          </Flex>
          <Flex
            alignItems="center"
            data-testid="positions-added-to-batch"
            data-cy="positions-added-to-batch"
          >
            <Flex>
              <Text className={fieldLabel}>
                <Button isInline displayAsLink className={linkButton}>
                  {`${selectedPositionsCount} ${plur("position", selectedPositionsCount)}`}
                </Button>{" "}
                totaling <Money money={selectedSum} rounded={false} /> have been added to this
                batch
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SelectPositionsStep;
